import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { api } from '../../api/client';
import { useIsAdmin } from '../../api/session';
import type { UserSummary } from '../../api/types';
import { useCrumbs } from '../../layout/crumbs';
import { Badge } from '../../ui/Badge';
import { LinkButton } from '../../ui/Button';
import { PageUnavailable, QueryView } from '../../ui/feedback';
import { PageHeader, usePageTitle } from '../../ui/misc';
import { Timestamp } from '../../ui/Timestamp';

export function UsersPage() {
  usePageTitle('Users');
  useCrumbs([{ label: 'Administration' }, { label: 'Users' }]);
  const admin = useIsAdmin();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<UserSummary[]>('/admin/users'),
    enabled: admin,
  });
  if (!admin) return <PageUnavailable />;
  return (
    <div className="page">
      <PageHeader
        title="Users"
        subtitle="People with a Jira role in Lixionary IAM. They join through invitations."
        actions={
          <LinkButton to="/admin/invitations" variant="primary">
            Invite people
          </LinkButton>
        }
      />
      <QueryView query={q}>
        {(users) => (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>State</th>
                  <th>Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="clickable" onClick={() => navigate(`/admin/users/${u.id}`)}>
                    <td>
                      <a
                        href={`/admin/users/${u.id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(`/admin/users/${u.id}`);
                        }}
                        className="primary-cell"
                      >
                        {u.displayName}
                      </a>
                      <div className="small muted">{u.email}</div>
                    </td>
                    <td>
                      {u.role === 'admin' ? (
                        <span className="chip">Admin</span>
                      ) : (
                        <span className="chip">Member</span>
                      )}
                    </td>
                    <td>
                      {!u.active ? (
                        <Badge>Inactive</Badge>
                      ) : u.iamState === 'invited' ? (
                        <Badge tone="info">Invited</Badge>
                      ) : (
                        <Badge tone="success">Active</Badge>
                      )}
                    </td>
                    <td className="small">
                      <Timestamp value={u.lastSignInAt} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </QueryView>
    </div>
  );
}
